import React, { useCallback, useRef, useImperativeHandle, FocusEventHandler } from 'react'
import TextAreaOnly from 'components/Common/Form/Input/TextAreaOnly'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import IconButton from 'LuxKit/Button/IconButton'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import styled from 'styled-components'
import AIGlowBorder from 'components/Search/SearchForm/AIGlowBorder'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'

/* Extended AIGlowBorder with focus-within box-shadow effect */
const StyledAIGlowBorder = styled(AIGlowBorder)`
  box-shadow: 0 0 ${rem(6)} transparent,
              0 0 ${rem(12)} transparent;
  transition: box-shadow 0.2s ease;

  &:focus-within {
    box-shadow: 0 0 ${rem(6)} ${props => props.theme.palette.product.ai.light35},
                0 0 ${rem(12)} ${props => props.theme.palette.product.ai.dark25};
  }
`

const Field = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(50)};
  transition: all 0.2s ease;
  overflow: hidden;
  background: white;
  border-radius: ${rem(2)};
  z-index: 1;

  &:focus-within {
    height: auto;
    overflow: visible;
  }
`

const DisplayField = styled(SearchFormField)`
  pointer-events: none;
  transition: opacity 0.2s;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border: none;
  width: auto;

  overflow: hidden;

  &.hidden {
    opacity: 0;
  }
`

const ClearButton = styled(IconButton)`
  margin-top: ${rem(4)};
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0s 0.2s;
`

const searchInputStyles = `
  height: 100%;
  min-height: ${rem(46)};
  border: none;

  > input {
    padding-left: ${rem(16)};
    height: 100%;
  }

  > textarea {
    padding-left: ${rem(16)};
    height: 100%;
    overflow: hidden;
  }

  /* Align icons to the top for textarea */
  > div {
    top: ${rem(2)};
    transform: none;
  }

  &:focus-within + ${DisplayField} {
    opacity: 0;
  }

  &:focus-within > *:not(:placeholder-shown) + div ${ClearButton} {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
    transition: opacity 0.2s, visibility 0s;
  }
`
const SearchInputTextArea = styled(TextAreaOnly)`${searchInputStyles}`

type BaseProps = Omit<React.ComponentProps<typeof TextAreaOnly>, 'noValidationSpacing' | 'noValidationMessage'>
interface Props extends Omit<BaseProps, 'onFocus' | 'ref'> {
  onFocus?: FocusEventHandler<HTMLTextAreaElement>
  ref?: React.Ref<HTMLTextAreaElement | HTMLInputElement>
  label?: string
  value?: string
  defaultValue?: string
  displayValue?: string
  onClear?: () => void
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}

function SearchFormMultiLineTextField(props: Props) {
  const {
    placeholder,
    className,
    label,
    value,
    displayValue,
    onClear = noop,
    required,
    onFocus,
    ref,
    onChange,
  } = props

  const localInputRef = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => localInputRef.current!)

  const onClearClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    if (localInputRef.current) {
      localInputRef.current.value = ''
      localInputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
      onClear()
    }
  }, [onClear])

  const handleFocus = useCallback<FocusEventHandler<HTMLTextAreaElement>>((event) => {
    if (event.currentTarget.value) {
      event.currentTarget.select()
    }
    onFocus?.(event)
  }, [onFocus])

  const clearButton = <ClearButton
    kind="tertiary"
    // Safari does not focus buttons when they are clicked, this causes the ';focus-within' to
    // be removed and the button does not click (it'll have pointer events: none)
    // So we need to capture the 'click' earlier via mouse down
    onMouseDown={onClearClick}
  >
    <LineTimesIcon />
  </ClearButton>

  return <StyledAIGlowBorder className={className}>
    <Field>
      <HiddenInput
        required={required}
        value={displayValue || value}
      />
      <SearchInputTextArea
        onChange={onChange}
        ref={localInputRef}
        noValidationSpacing
        noValidationMessage
        placeholder={placeholder}
        onFocus={handleFocus}
        value={value}
        endIcon={clearButton}
        resize="none"
      />
      <DisplayField
        label={label}
        placeholder={placeholder}
        value={displayValue || value}
        className="display-field"
      />
    </Field>
  </StyledAIGlowBorder>
}

export default SearchFormMultiLineTextField
